// OP-3605 — Regras puras do inventário, espelho de `domain/cadastro.ts`
// (`podeAbrirInventario`, `divergenciasDe`, `podeFecharContagem`,
// `naoEncontrada`, `podeAprovarAjuste`). Gates visuais.
import { InventarioLinha, InventarioRow, InventarioUnidade, StatusInventario } from '../../../../types/estoque'

export const STATUS_INVENTARIO_UI: Record<StatusInventario, { bg: string; color: string; label: string }> = {
  EM_CONTAGEM: { bg: '#E0E7FF', color: '#3730A3', label: 'Em contagem' },
  AGUARDANDO_APROVACAO: { bg: '#FEF3C7', color: '#92400E', label: 'Aguardando aprovação' },
  FECHADO: { bg: '#F3F4F6', color: '#374151', label: 'Fechado' },
  DESCARTADO: { bg: '#F3F4F6', color: '#6B7280', label: 'Descartada' },
}

/**
 * Marcar é achar — espelho de `naoEncontrada(encontrada) = encontrada !== true`.
 *
 * "Marcada como ausente" e "ninguém tocou" são a MESMA coisa para o servidor.
 * Por isso a tela tem um toggle só por peça, e não três estados: o segundo
 * botão sugeriria uma diferença que não existe, e o rodapé avisa que tudo que
 * ficar sem marca vira extravio no fechamento.
 */
export const foiEncontrada = (u: Pick<InventarioUnidade, 'encontrada'>): boolean => u.encontrada === true

/** Quantas peças da linha foram achadas. O `contado` de linha SERIAL é derivado, nunca digitado. */
export const contadasNaLinha = (l: InventarioLinha): number =>
  (l.unidades ?? []).filter(foiEncontrada).length

/** A linha de quantidade ainda está em branco? Em branco não é zero. */
export const emBranco = (l: InventarioLinha): boolean =>
  l.controle === 'QUANTIDADE' && l.contado == null

/**
 * Espelho de `podeFecharContagem`: nada a contar, ou alguma linha de
 * quantidade ainda em branco. Linha SERIAL nunca trava o fechamento — ela
 * nasce em zero e "não marcada" já é uma resposta.
 */
export function impedimentoFecharContagem(linhas: InventarioLinha[]): string | null {
  if (!linhas.length) return 'Este inventário não tem linhas para contar.'
  const faltam = linhas.filter(emBranco)
  if (faltam.length) {
    return `Faltam ${faltam.length} ${faltam.length === 1 ? 'item' : 'itens'} por contar: ${faltam.slice(0, 3).map((l) => l.nome).join(', ')}${faltam.length > 3 ? '…' : ''}. Em branco não é zero.`
  }
  return null
}

/** Divergência = contado informado e diferente do esperado (espelho de `divergenciasDe`). */
export function diferencaDaLinha(l: InventarioLinha): number | null {
  const contado = l.controle === 'SERIAL' ? contadasNaLinha(l) : l.contado
  if (contado == null) return null
  const d = contado - Number(l.esperado)
  return d === 0 ? null : d
}

/** Quantas peças da linha não foram achadas — viram extravio na aprovação. */
export const naoEncontradasNaLinha = (l: InventarioLinha): number =>
  (l.unidades ?? []).filter((u) => !foiEncontrada(u)).length

export interface ContextoAprovacao {
  status: StatusInventario
  contadoPorId: number
  /** Cumulativa: quem abriu, contou uma linha ou marcou uma peça. */
  participantesIds?: number[]
  usuarioId: number | null
  podeAprovar: boolean
  motivo: string
}

/**
 * Participou da contagem? Espelho de `est_inventario_participante`, que é
 * cumulativo — guardar só o último em `contado_por_id` não bastava: 42 conta,
 * 43 repete o mesmo valor, e 42 voltava a poder aprovar.
 */
export function participou(c: Pick<ContextoAprovacao, 'contadoPorId' | 'participantesIds' | 'usuarioId'>): boolean {
  if (c.usuarioId == null) return false
  if (Number(c.contadoPorId) === c.usuarioId) return true
  return (c.participantesIds ?? []).map(Number).indexOf(c.usuarioId) >= 0
}

/**
 * Espelho de `podeAprovarAjuste`, na ordem do domínio.
 *
 * A participação entra aqui inteira: sem ela, quem digitou uma linha podia
 * preencher o motivo, clicar em aprovar e receber 403 — o servidor virava a
 * forma de descobrir a regra, em vez de ser a última defesa dela.
 */
export function impedimentoAprovarInventario(c: ContextoAprovacao): string | null {
  if (c.status !== 'AGUARDANDO_APROVACAO') return 'Só se aprova inventário que fechou com divergência.'
  if (!c.podeAprovar) return 'Aprovar ajuste de inventário é alçada da coordenação (permissão "Aprovar inventário").'
  if (participou(c)) {
    return Number(c.contadoPorId) === c.usuarioId
      ? 'Você abriu esta contagem: quem conta não aprova. Peça a aprovação a outra pessoa.'
      : 'Você participou desta contagem: quem conta não aprova. Peça a aprovação a outra pessoa.'
  }
  if (!c.motivo.trim()) return 'Explique o ajuste: o motivo fica no histórico.'
  return null
}

export function impedimentoDescartarInventario(c: Omit<ContextoAprovacao, 'podeAprovar'> & { podeDescartar: boolean }): string | null {
  if (c.status !== 'EM_CONTAGEM' && c.status !== 'AGUARDANDO_APROVACAO') return 'Esta contagem já foi encerrada.'
  if (!c.podeDescartar) return 'Descartar contagem é alçada da coordenação (permissão "Aprovar inventário").'
  if (c.status === 'AGUARDANDO_APROVACAO' && participou(c)) {
    return 'Você participou desta contagem: quem conta não descarta a divergência que achou. Peça a outra pessoa.'
  }
  if (!c.motivo.trim()) return 'Escreva o motivo do descarte: ele fica gravado na contagem.'
  return null
}

/** Reabrir só sai de AGUARDANDO_APROVACAO — e é destrutivo. */
export function impedimentoReabrir(status: StatusInventario): string | null {
  return status === 'AGUARDANDO_APROVACAO' ? null : 'Só se reabre um inventário que está aguardando aprovação.'
}

/** Uma contagem aberta por praça (espelho de `podeAbrirInventario`). */
export function pracaComInventarioAberto(inventarios: InventarioRow[], localId: number): InventarioRow | null {
  return inventarios.find((i) => i.localId === localId && i.status !== 'FECHADO' && i.status !== 'DESCARTADO') ?? null
}

/** A quantidade que o servidor aceita: inteiro ≥ 0. Em branco, null e espaço não passam. */
export function quantidadeContadaValida(v: string): boolean {
  const s = v.trim()
  if (s === '') return false
  if (!/^\d+$/.test(s)) return false
  return Number.isInteger(Number(s))
}
